import { useEffect } from "react"
import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { ContactBand, PhotoEnquiryBand } from "@/components/gisela/ClosingBands"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import { useLocalePath } from "@/lib/locale-path"
import { bandBg } from "@/components/gisela/TextBand"
import { sectionHidden } from "@/lib/section-visibility"
import { cn } from "@/lib/utils"
import type { PageMeta, PageProps, PlacementPhoto } from "@/types/inertia"

interface HelpItem {
  id: string
  name: string
  blurb: string
  // null for listed-only conditions: this page is the only place they are
  // described, so there is nowhere further to read.
  path: string | null
  // The picture chosen for this condition, else the cover of the first
  // article written for it, else null — the row then carries the site's
  // empty-photo tint.
  photo: { card_url: string | null } | null
}

interface HelpGroup {
  key: string
  heading?: string
  items: HelpItem[]
}

interface Props {
  content: {
    hero: { heading?: string; subheading?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    // The closing invitation: fixed copy, only its background photo is
    // editable — the same arrangement the blog's contact band has.
    cta?: { photo?: HeroPhoto | null; _hidden?: boolean }
    // The closing photo-and-form band: admin copy, the photo arrives in its
    // own prop as every placement photo does.
    contact?: { heading?: string; body?: string; _hidden?: boolean }
    groups: HelpGroup[]
  }
  // The home page's tile slot, read here too: one row of everywhere else on
  // her site, minus this page.
  tile_photos?: Record<string, PlacementPhoto | null>
  // A room of hers, portrait, standing beside the form.
  contact_photo?: PlacementPhoto | null
  meta?: PageMeta
}

// The index of everything Gisela works with: her condition categories and
// their conditions, a row apiece and nothing hidden — the same inventory the
// nav dropdown shows. Entries with a page of their own link on to it; the
// rest are described here and nowhere else, which is why the menu links them
// to their anchor on this page.
export default function WhatICanHelpWith({ content, tile_photos = {}, contact_photo, meta }: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.help
  const lp = useLocalePath()
  const title = meta?.title ?? t.meta_title
  const description = meta?.description ?? t.meta_description
  const groups = content.groups.filter((group) => group.items.length > 0)

  // Arriving from the menu's "Other difficulties" flyout on a listed-only
  // condition (/what-i-can-help-with#self-harm) should land on that entry.
  // Every row is open, so this is only the scroll: Inertia restores its own
  // scroll position on a visit, which is not the anchor the visitor asked
  // for.
  useEffect(() => {
    function scrollToHashEntry() {
      const id = decodeURIComponent(window.location.hash.slice(1))
      if (!id) return

      document.getElementById(id)?.scrollIntoView({ block: "start" })
    }

    scrollToHashEntry()
    // Picking another listing from the menu while already on this page is a
    // same-document navigation: the hash changes and nothing remounts.
    window.addEventListener("hashchange", scrollToHashEntry)
    return () => window.removeEventListener("hashchange", scrollToHashEntry)
  }, [])

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {/* Hero */}
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo}>
            <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">{t.eyebrow}</p>
            <h1 className="text-white">{content.hero.heading || t.hero_heading}</h1>
            {content.hero.subheading && (
              <p className="mt-4 max-w-2xl text-base text-white/80 sm:text-lg">{content.hero.subheading}</p>
            )}
          </HeroBand>
        )}

        {/* Condition groups */}
        {groups.map((group, index) => (
          <section key={group.key} className={cn(bandBg(index), "py-10 lg:py-14")}>
            <div className="mx-auto max-w-6xl px-6 lg:px-8">
              {group.heading && <h2>{group.heading}</h2>}
              {group.items.map((item, i) => (
                <div
                  key={item.id}
                  id={item.id}
                  className={cn(
                    "grid scroll-mt-24 grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-x-6 gap-y-4 py-8 sm:grid-cols-[8.5rem_minmax(0,5fr)_minmax(0,7fr)] sm:gap-x-10 lg:py-10",
                    i === 0 ? "mt-2" : "border-t border-hairline"
                  )}
                >
                  {/* The condition's card picture, or the cover borrowed from
                      its first article. A row with neither keeps its square as
                      a tint, so the list stays a column. The listed-only rows
                      have a picture but no page, so their square is not a
                      link. */}
                  <ConditionSquare item={item} path={item.path ? lp(item.path) : null} />

                  <div>
                    <h3>{item.path ? <a href={lp(item.path)}>{item.name}</a> : item.name}</h3>
                    {item.path && (
                      <p className="mt-2">
                        <a href={lp(item.path)} className="text-sm font-medium text-teal">
                          {t.read_more}
                        </a>
                      </p>
                    )}
                  </div>

                  {/* On a phone the description reads under the name rather than
                      beside it — a third column at that width is a word wide. */}
                  {item.blurb && (
                    <div className="body-content col-span-2 text-ink-muted sm:col-span-1">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          // Keep any links inside the blurb on the visitor's language.
                          a: ({ href, children }) => <a href={lp(href ?? "")}>{children}</a>,
                        }}
                      >
                        {item.blurb}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}

        {/* The closing pair the home page ends on — the teal invitation, then
            the tiles — rather than the pale centred CTA this page used to
            finish with. The words stay this page's own: a reader who has just
            been through the list of difficulties is asking whether theirs
            counts, not where to start. */}
        {!sectionHidden(content.cta) && (
          <ContactBand heading={t.cta_heading} body={t.cta_body} photo={content.cta?.photo} />
        )}

        {/* This page's own tile is left out — the reader is on it. */}
        <GiselaPageTiles photos={tile_photos} exclude={["/what-i-can-help-with"]} />

        {/* A room beside a short form, last on the page. Someone who has read
            the whole list and still isn't certain theirs is on it needs to be
            able to just ask. */}
        {!sectionHidden(content.contact) && (
          <PhotoEnquiryBand
            heading={content.contact?.heading}
            body={content.contact?.body}
            photo={contact_photo}
          />
        )}
      </PublicLayout>
    </>
  )
}

// The square beside each row: the cover of the condition's own article, or
// the tint the rest of the site uses for an empty photo slot where there is
// no article to borrow one from. Where the row reads on, the picture is part
// of that link — but hidden from screen readers, which have the name beside
// it and do not need the same destination twice.
function ConditionSquare({ item, path }: { item: HelpItem; path: string | null }) {
  const square = item.photo?.card_url ? (
    <img
      src={item.photo.card_url}
      alt=""
      className="aspect-square w-full rounded-2xl object-cover ring-1 ring-hairline"
      loading="lazy"
      decoding="async"
    />
  ) : (
    <span className="block aspect-square w-full rounded-2xl bg-sage-light/40 ring-1 ring-hairline" />
  )

  if (!path) return square

  return (
    <a href={path} tabIndex={-1} aria-hidden="true" className="block">
      {square}
    </a>
  )
}
